/* eslint-disable no-use-before-define */
import { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from 'uxd-design-system-components';
import { useLocation } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import getText from '../../../assets/locale/locale';
import LocaleContext from '../../../contexts/LocaleContext';
import './WelcomePage.scss';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import StyledModal from '../../../components/StyledModal/StyledModal';
import WelcomePageProps from '~types/pages/WelcomePageProps';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';

/**
 * WelcomePage is the introductory screen for the quiz
 */
const WelcomePage = (props: WelcomePageProps) => {
  const { contactName, customizeBtnCallback, isLoading } = props;
  const locId = useContext(LocaleContext);
  const [showModal, setShowModal] = useState(false);
  const modalHeaderText = getText(locId, 'modalTitle', null);
  const modalParagraphText1 = getText(locId, 'modalParagraph1', null);
  const modalParagraphText2 = getText(locId, 'modalParagraph2', null);

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'welcomePage');
  const pageReferrer = previousPath ? `${window.location.origin}${previousPath}${currentLocation.search}` : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    'pagePath' : currentLocation.pathname,
    'pageTitle': pageTitle,
    'page_referrer': pageReferrer,
    'external_referrer': window.document.referrer && window.document.referrer.trim() !== '' ? window.document.referrer : undefined,
    'page_url': pageURL
  };

  const { dataLayerInfo } =  useDataLayerInfo();
  const updatesFPPlanningTopics = {
    ...dataLayerInfo,
    'page_type': 'welcome-page',
  };

  useEffect(() => {
    document.title = pageTitle;
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setPreviousPath(currentLocation.pathname);
      addDataLayerTrigger('dataLayer-initialized', updatesFPPlanningTopics);
      addDataLayerTrigger('VirtualPageview', virtualPageInfo);
      dataLayerScroll();
    }
  }, [isLoading]);

  function dataLayerScroll() {
    let timeout: number;
    window.addEventListener(
      'scroll',
      () => {
        if (timeout) {
          window.clearTimeout(timeout);
        }
        timeout = window.setTimeout(scrollEvent, 100);
      },
      true
    );
  }

  function scrollEvent() {
    const footerLocation = document
      .querySelector('footer')
      .getBoundingClientRect();
    if (
      footerLocation.top >= 0 &&
      footerLocation.left >= 0 &&
      footerLocation.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      footerLocation.right <=
        (window.innerWidth || document.documentElement.clientWidth)
    ) {
      addDataLayerTrigger('footer-visible', {});
    }
  }

  return isLoading ? (
    <div id="spinner">
      <Spinner animation="border" />
    </div>
  ) : (
    <>
      <div className="WelcomePage" data-testid="WelcomePage">
        <div className="WelcomePage_content">
          <div className="WelcomePage_left">
            <div className="WelcomePage_left_container">
              <h1 id="heading-no-focus" className="heading--semi-bold">
                {getText(locId, 'header', 'welcomePage')}
                {contactName.firstName ? ` ${contactName.firstName}` : null}
                !
              </h1>
              <p className="WelcomePage_p">
                {getText(locId, 'introPa', 'welcomePage')}
              </p>
              <p className="WelcomePage_p WelcomePage_p--secondary">
                {getText(locId, 'customizeInstr', 'welcomePage')}
              </p>
              <Button
                label={getText(locId, 'startBtnLabel', 'welcomePage')}
                styleVariant="primary"
                onClick={() => {
                  addDataLayerTrigger('to-customize-page', {});
                  customizeBtnCallback();
                }}
                attributes={{
                  value: '',
                  id: 'customize-quiz-button',
                }}
              />
            </div>
          </div>
          <div className="WelcomePage_right">
            <div className="image-frame">
              <img
                src="/public/images/welcome_image.png"
                alt=""
                role="presentation"
              />
              <div className="modal-button-wrapper">
                <div
                  className="modal-button"
                  role="button"
                  onClick={() => {
                    setShowModal(true);
                  }}
                  onKeyDown={event => {
                    if (event.keyCode === 13 || event.keyCode === 32) {
                      setShowModal(true);
                    }
                  }}
                  tabIndex={0}
                >
                  <Icon name="help-question-mark" />
                  <p className="modal-button-text">
                    {getText(locId, 'modalTitle', null)}
                  </p>
                </div>
              </div>
            </div>
            <StyledModal
              displayModal={showModal}
              closeModalHandler={() => setShowModal(false)}
              headerText={modalHeaderText}
              paragraphText1={modalParagraphText1}
              paragraphText2={modalParagraphText2}
            />
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="disclaimer">
          <p className="microcopy--2">
            {getText(locId, 'welcomePageDisclosure', 'welcomePage')}
          </p>
        </div>
      </div>
    </>
  );
};

WelcomePage.defaultProps = {
  contactName: { firstNa: '', lastNa: '' },
};

WelcomePage.propTypes = {
  contactName: PropTypes.objectOf(PropTypes.string),
  // eslint-disable-next-line react/no-unused-prop-types
  customizeBtnCallback: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default WelcomePage;
